/**
 * Flyer V2 — herramienta propia del Centro Comercial.
 * Flujo: Producto → Diseño → Personalizar → Publicar.
 * Reutiliza catálogo, precios, empresaConfig y export html2canvas.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaArrowLeft,
  FaDownload,
  FaImage,
  FaShareAlt,
  FaStore,
  FaCheck
} from 'react-icons/fa';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import Card from '../../../components/common/Card';
import Button from '../../../components/common/Button';
import Spinner from '../../../components/common/Spinner';
import SearchBar from '../../../components/common/SearchBar';
import useEmpresaConfig from '../../../hooks/useEmpresaConfig';
import productosService from '../../../services/productos.service';
import promocionesService from '../../../services/promociones.service';
import { filtrarProductosPorBusqueda } from '../../../utils/productoBusqueda';
import { LISTA_PRECIO_KEYS, etiquetaLista } from '../../../utils/listasPreciosLabels';
import { exportNodeAsImage, shareImageIfPossible } from '../../../utils/listasExpresExport';
import FlyerCanvas from './FlyerCanvas';
import { FLYER_FORMATS, getFlyerFormat } from './flyerFormats';
import {
  FLYER_CTA_PRESETS,
  FLYER_TEMPLATES,
  FLYER_TITLE_PRESETS,
  compatibleFlyerTemplates,
  suggestFlyerTemplate
} from './flyerTemplates';
import {
  buildFlyerBrand,
  buildFlyerSubjectFromProduct,
  normalizePromoFromAplicar,
  resolveFlyerBrandColors,
  resolveProductImageUrl
} from './flyerModel';

const STEPS = [
  { id: 'producto', label: 'Producto' },
  { id: 'diseno', label: 'Diseño' },
  { id: 'personalizar', label: 'Personalizar' },
  { id: 'publicar', label: 'Publicar' }
];

async function exportNodeAsPdf(node, { filename = 'flyer', width, height } = {}) {
  if (!node) throw new Error('Preview no disponible');
  const canvas = await html2canvas(node, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    backgroundColor: null,
    logging: false
  });
  const img = canvas.toDataURL('image/png');
  const orientation = width > height ? 'landscape' : 'portrait';
  const pdf = new jsPDF({
    orientation,
    unit: 'px',
    format: [width, height],
    hotfixes: ['px_scaling']
  });
  pdf.addImage(img, 'PNG', 0, 0, width, height);
  pdf.save(`${filename}.pdf`);
  return { ok: true };
}

const FlyerStudio = () => {
  const canvasRef = useRef(null);
  const fileRef = useRef(null);
  const { empresaConfig, loading: loadingEmpresa } = useEmpresaConfig();

  const [step, setStep] = useState('producto');
  const [loading, setLoading] = useState(true);
  const [productos, setProductos] = useState([]);
  const [search, setSearch] = useState('');
  const [product, setProduct] = useState(null);
  const [overrideImage, setOverrideImage] = useState('');
  const [imageFit, setImageFit] = useState('contain');
  const [priceListKey, setPriceListKey] = useState('mayorista');
  const [templateKey, setTemplateKey] = useState('impacto');
  const [formatKey, setFormatKey] = useState('cuadrado');
  const [title, setTitle] = useState('OFERTA');
  const [subtitle, setSubtitle] = useState('');
  const [cta, setCta] = useState('Consultanos');
  const [colorPrimary, setColorPrimary] = useState('');
  const [colorSecondary, setColorSecondary] = useState('');
  const [showLogo, setShowLogo] = useState(true);
  const [showContact, setShowContact] = useState(true);
  const [showQr, setShowQr] = useState(true);
  const [showPrice, setShowPrice] = useState(true);
  const [showCompare, setShowCompare] = useState(true);
  const [showPromo, setShowPromo] = useState(true);
  const [promo, setPromo] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await productosService.obtenerTodos();
        if (!cancelled) setProductos(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error(err);
        toast.error('No se pudo cargar el catálogo');
        if (!cancelled) setProductos([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const colors = resolveFlyerBrandColors(empresaConfig || {});
    if (!colorPrimary) setColorPrimary(colors.primary);
    if (!colorSecondary) setColorSecondary(colors.secondary);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaConfig]);

  const subject = useMemo(
    () =>
      buildFlyerSubjectFromProduct(product, {
        priceListKey,
        overrideImage
      }),
    [product, priceListKey, overrideImage]
  );

  const brand = useMemo(
    () =>
      buildFlyerBrand(empresaConfig, {
        colors: { primary: colorPrimary, secondary: colorSecondary },
        showLogo,
        showContact,
        showQr
      }),
    [empresaConfig, colorPrimary, colorSecondary, showLogo, showContact, showQr]
  );

  const hasImage = Boolean(subject?.hasImage);
  const templates = useMemo(() => compatibleFlyerTemplates(hasImage), [hasImage]);

  useEffect(() => {
    if (!product) return;
    const suggested = suggestFlyerTemplate(hasImage);
    if (!templates.some((t) => t.key === templateKey)) {
      setTemplateKey(suggested.key);
    }
  }, [product, hasImage, templates, templateKey]);

  const loadPromo = useCallback(
    async (prod, listKey) => {
      if (!prod?.id) {
        setPromo(null);
        return;
      }
      const price = buildFlyerSubjectFromProduct(prod, { priceListKey: listKey })?.price
        ?.amount;
      if (!price) {
        setPromo(null);
        return;
      }
      try {
        const response = await promocionesService.aplicarPromociones([
          {
            id: prod.id,
            producto_id: prod.id,
            codigo: prod.codigo,
            nombre: prod.nombre,
            precio: Math.round(price * 100) / 100,
            cantidad: 1,
            subtotal: Math.round(price * 100) / 100,
            tipoVenta: 'unidad'
          }
        ]);
        const item = Array.isArray(response?.data) ? response.data[0] : null;
        setPromo(normalizePromoFromAplicar(item));
      } catch {
        setPromo(null);
      }
    },
    []
  );

  const selectProduct = async (p) => {
    setProduct(p);
    setOverrideImage('');
    const img = resolveProductImageUrl(p, '');
    const suggested = suggestFlyerTemplate(Boolean(img));
    setTemplateKey(suggested.key);
    setStep('diseno');
    await loadPromo(p, priceListKey);
  };

  useEffect(() => {
    if (product) loadPromo(product, priceListKey);
  }, [priceListKey, product, loadPromo]);

  const filtered = useMemo(() => {
    const term = search.trim();
    if (!term) return (productos || []).slice(0, 40);
    return filtrarProductosPorBusqueda(productos, term).slice(0, 40);
  }, [productos, search]);

  const onPickImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Elegí una imagen válida');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setOverrideImage(String(reader.result || ''));
      toast.success('Imagen aplicada solo a esta pieza');
    };
    reader.onerror = () => toast.error('No se pudo leer la imagen');
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const filenameBase = useMemo(() => {
    const slug = String(subject?.name || 'flyer')
      .toLowerCase()
      .replace(/[^a-z0-9]+/gi, '-')
      .slice(0, 40);
    return `flyer-${slug || 'pieza'}`;
  }, [subject]);

  const doExport = async (kind) => {
    if (!subject) {
      toast.info('Elegí un producto primero');
      setStep('producto');
      return;
    }
    const node = canvasRef.current;
    if (!node) {
      toast.error('Preview no disponible');
      return;
    }
    setExporting(true);
    try {
      const format = getFlyerFormat(formatKey);
      if (kind === 'pdf') {
        await exportNodeAsPdf(node, {
          filename: filenameBase,
          width: format.width,
          height: format.height
        });
      } else {
        await exportNodeAsImage(node, {
          format: kind,
          filename: filenameBase,
          scale: 2
        });
      }
      toast.success(`Exportado ${String(kind).toUpperCase()}`);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Error al exportar');
    } finally {
      setExporting(false);
    }
  };

  const doShare = async () => {
    if (!canvasRef.current || !subject) return;
    setExporting(true);
    try {
      const result = await shareImageIfPossible(canvasRef.current, {
        title: title || subject.name,
        text: `${title} — ${subject.name}`
      });
      if (!result.ok) toast.info('Compartir no disponible en este dispositivo; usá PNG/JPG');
      else toast.success('Listo para compartir');
    } catch (err) {
      toast.error(err?.message || 'No se pudo compartir');
    } finally {
      setExporting(false);
    }
  };

  const preview = (
    <FlyerCanvas
      ref={canvasRef}
      subject={subject}
      brand={brand}
      templateKey={templateKey}
      formatKey={formatKey}
      title={title}
      subtitle={subtitle}
      cta={cta}
      showPrice={showPrice}
      showCompare={showCompare}
      showPromo={showPromo}
      promo={promo}
      imageFit={imageFit}
    />
  );

  return (
    <div className="space-y-6" data-testid="flyer-studio">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-sm text-e0e3-muted mb-2">
            <Link to="/centro-comercial" className="hover:text-e0e3-accent-soft inline-flex items-center gap-1">
              <FaArrowLeft /> Centro Comercial
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <FaImage className="text-e0e3-accent text-2xl" />
            <h1 className="text-2xl sm:text-3xl font-bold text-e0e3-foreground">Flyer</h1>
          </div>
          <p className="text-e0e3-muted mt-1 max-w-xl">
            Piezas listas para WhatsApp, redes o impresión con tus productos reales.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {STEPS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setStep(s.id)}
              className={`min-h-[40px] px-3 rounded-lg text-sm font-medium border ${
                step === s.id
                  ? 'border-e0e3-accent bg-e0e3-accent/20 text-e0e3-foreground'
                  : 'border-white/10 text-e0e3-muted hover:border-white/25'
              }`}
              data-testid={`flyer-step-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="space-y-4 order-2 xl:order-1">
          {step === 'producto' && (
            <Card>
              <h2 className="text-lg font-semibold text-e0e3-foreground mb-1">
                ¿Qué querés promocionar?
              </h2>
              <p className="text-sm text-e0e3-muted mb-4">
                Buscá en tu catálogo. No se crea un catálogo paralelo.
              </p>
              <SearchBar
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nombre, código o barras…"
              />
              <div className="mt-3">
                <label className="text-xs text-e0e3-muted">Lista de precio</label>
                <select
                  className="mt-1 w-full rounded-lg bg-black/20 border border-white/10 px-3 py-2 text-e0e3-foreground"
                  value={priceListKey}
                  onChange={(e) => setPriceListKey(e.target.value)}
                >
                  {LISTA_PRECIO_KEYS.map((k) => (
                    <option key={k} value={k}>
                      {etiquetaLista(empresaConfig, k)}
                    </option>
                  ))}
                </select>
              </div>
              {loading || loadingEmpresa ? (
                <div className="flex justify-center py-10">
                  <Spinner />
                </div>
              ) : (
                <ul className="mt-4 max-h-[420px] overflow-auto divide-y divide-white/5" data-testid="flyer-product-list">
                  {filtered.map((p) => {
                    const img = resolveProductImageUrl(p, '');
                    const selected = product?.id === p.id;
                    return (
                      <li key={p.id}>
                        <button
                          type="button"
                          onClick={() => selectProduct(p)}
                          className={`w-full text-left flex items-center gap-3 py-3 px-2 min-h-[56px] hover:bg-white/[0.04] ${
                            selected ? 'bg-e0e3-accent/15' : ''
                          }`}
                        >
                          <div className="w-12 h-12 rounded-lg overflow-hidden bg-white/5 shrink-0 flex items-center justify-center">
                            {img ? (
                              <img src={img} alt="" className="w-full h-full object-contain" />
                            ) : (
                              <FaStore className="text-e0e3-muted" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-e0e3-foreground truncate">{p.nombre}</div>
                            <div className="text-xs text-e0e3-muted truncate">
                              {p.codigo || 'Sin código'} ·{' '}
                              {buildFlyerSubjectFromProduct(p, { priceListKey })?.price?.formatted ||
                                'Sin precio'}
                            </div>
                          </div>
                          {selected ? <FaCheck className="text-e0e3-ok shrink-0" /> : null}
                        </button>
                      </li>
                    );
                  })}
                  {!filtered.length ? (
                    <li className="py-8 text-center text-e0e3-muted text-sm">
                      No hay productos para ese criterio.
                    </li>
                  ) : null}
                </ul>
              )}
            </Card>
          )}

          {step === 'diseno' && (
            <Card>
              <h2 className="text-lg font-semibold text-e0e3-foreground mb-1">Elegí un diseño</h2>
              <p className="text-sm text-e0e3-muted mb-4">
                {hasImage
                  ? 'Plantillas con y sin foto disponibles.'
                  : 'Sin foto del producto: se priorizan diseños tipográficos.'}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="flyer-templates">
                {templates.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTemplateKey(t.key)}
                    className={`text-left rounded-xl border p-4 min-h-[88px] ${
                      templateKey === t.key
                        ? 'border-e0e3-accent bg-e0e3-accent/15'
                        : 'border-white/10 hover:border-white/25'
                    }`}
                  >
                    <div className="font-semibold text-e0e3-foreground">{t.label}</div>
                    <div className="text-sm text-e0e3-muted mt-1">{t.blurb}</div>
                  </button>
                ))}
              </div>
              <div className="mt-5">
                <div className="text-sm font-medium text-e0e3-foreground mb-2">Formato</div>
                <div className="flex flex-wrap gap-2">
                  {FLYER_FORMATS.map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => setFormatKey(f.key)}
                      className={`min-h-[40px] px-3 rounded-lg text-sm border ${
                        formatKey === f.key
                          ? 'border-e0e3-accent bg-e0e3-accent/20'
                          : 'border-white/10'
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                <Button color="secondary" onClick={() => setStep('producto')}>
                  Cambiar producto
                </Button>
                <Button color="primary" onClick={() => setStep('publicar')} disabled={!product}>
                  Continuar a publicar
                </Button>
                <Button color="secondary" onClick={() => setStep('personalizar')}>
                  Personalizar
                </Button>
              </div>
            </Card>
          )}

          {step === 'personalizar' && (
            <Card className="space-y-4">
              <h2 className="text-lg font-semibold text-e0e3-foreground">Personalizar</h2>
              <p className="text-sm text-e0e3-muted">Opcional — el modo rápido ya completa marca y precio.</p>

              <div>
                <label className="text-xs text-e0e3-muted">Título comercial</label>
                <div className="flex flex-wrap gap-2 mt-1 mb-2">
                  {FLYER_TITLE_PRESETS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className="text-xs px-2 py-1 rounded border border-white/10"
                      onClick={() => setTitle(p)}
                    >
                      {p}
                    </button>
                  ))}
                </div>
                <input
                  className="w-full rounded-lg bg-black/20 border border-white/10 px-3 py-2"
                  value={title}
                  onChange={(e) => setTitle(e.target.value.slice(0, 40))}
                  maxLength={40}
                />
              </div>

              <div>
                <label className="text-xs text-e0e3-muted">Texto secundario</label>
                <input
                  className="w-full rounded-lg bg-black/20 border border-white/10 px-3 py-2 mt-1"
                  value={subtitle}
                  onChange={(e) => setSubtitle(e.target.value.slice(0, 80))}
                  placeholder="Solo por esta semana"
                  maxLength={80}
                />
              </div>

              <div>
                <label className="text-xs text-e0e3-muted">CTA</label>
                <div className="flex flex-wrap gap-2 mt-1 mb-2">
                  {FLYER_CTA_PRESETS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className="text-xs px-2 py-1 rounded border border-white/10"
                      onClick={() => setCta(p)}
                    >
                      {p}
                    </button>
                  ))}
                </div>
                <input
                  className="w-full rounded-lg bg-black/20 border border-white/10 px-3 py-2"
                  value={cta}
                  onChange={(e) => setCta(e.target.value.slice(0, 48))}
                  maxLength={48}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-e0e3-muted">Color principal</label>
                  <input
                    type="color"
                    className="w-full h-10 mt-1 rounded cursor-pointer"
                    value={colorPrimary || '#0F766E'}
                    onChange={(e) => setColorPrimary(e.target.value)}
                    data-testid="flyer-color-primary"
                  />
                </div>
                <div>
                  <label className="text-xs text-e0e3-muted">Color secundario</label>
                  <input
                    type="color"
                    className="w-full h-10 mt-1 rounded cursor-pointer"
                    value={colorSecondary || '#F59E0B'}
                    onChange={(e) => setColorSecondary(e.target.value)}
                    data-testid="flyer-color-secondary"
                  />
                </div>
              </div>
              <p className="text-xs text-e0e3-muted">
                Colores de sesión. Si la empresa ya tiene `color_marca_primario` /
                `color_marca_secundario`, se usan como base (sin escritura automática).
              </p>

              <div className="flex flex-wrap gap-4 text-sm">
                {[
                  ['Logo', showLogo, setShowLogo],
                  ['Contacto', showContact, setShowContact],
                  ['QR', showQr, setShowQr],
                  ['Precio', showPrice, setShowPrice],
                  ['Precio anterior', showCompare, setShowCompare],
                  ['Promoción', showPromo, setShowPromo]
                ].map(([label, val, setter]) => (
                  <label key={label} className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={Boolean(val)}
                      onChange={(e) => setter(e.target.checked)}
                    />
                    {label}
                  </label>
                ))}
              </div>

              {promo ? (
                <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm">
                  Promo detectada: <strong>{promo.nombre}</strong>
                  {promo.badge ? ` · ${promo.badge}` : ''}
                  {promo.vigencia ? ` · hasta ${promo.vigencia}` : ''}
                </div>
              ) : (
                <div className="text-xs text-e0e3-muted">
                  Sin promoción activa aplicable vía motor existente (no se inventa descuento).
                </div>
              )}

              <div className="space-y-2">
                <div className="text-sm font-medium">Imagen de la pieza</div>
                <div className="flex flex-wrap gap-2">
                  <Button color="secondary" onClick={() => fileRef.current?.click()}>
                    Cambiar imagen para esta pieza
                  </Button>
                  {overrideImage ? (
                    <Button color="secondary" onClick={() => setOverrideImage('')}>
                      Usar imagen del producto
                    </Button>
                  ) : null}
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={onPickImage}
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className={`text-xs px-2 py-1 rounded border ${
                      imageFit === 'contain' ? 'border-e0e3-accent' : 'border-white/10'
                    }`}
                    onClick={() => setImageFit('contain')}
                  >
                    Contener
                  </button>
                  <button
                    type="button"
                    className={`text-xs px-2 py-1 rounded border ${
                      imageFit === 'cover' ? 'border-e0e3-accent' : 'border-white/10'
                    }`}
                    onClick={() => setImageFit('cover')}
                  >
                    Cubrir
                  </button>
                </div>
                <p className="text-xs text-e0e3-muted">
                  La imagen alternativa no modifica el catálogo. Sin crop interactivo en V2.
                </p>
              </div>

              <Button color="primary" onClick={() => setStep('publicar')}>
                Ir a publicar
              </Button>
            </Card>
          )}

          {step === 'publicar' && (
            <Card className="space-y-4">
              <h2 className="text-lg font-semibold text-e0e3-foreground">Publicar</h2>
              <p className="text-sm text-e0e3-muted">
                Exportá PNG, JPG o PDF listo para WhatsApp o redes.
              </p>
              {!subject ? (
                <p className="text-amber-200 text-sm">Elegí un producto para comenzar.</p>
              ) : null}
              <div className="flex flex-wrap gap-2">
                <Button
                  color="primary"
                  icon={<FaDownload />}
                  disabled={!subject || exporting}
                  onClick={() => doExport('png')}
                >
                  PNG
                </Button>
                <Button
                  color="secondary"
                  icon={<FaDownload />}
                  disabled={!subject || exporting}
                  onClick={() => doExport('jpg')}
                >
                  JPG
                </Button>
                <Button
                  color="secondary"
                  icon={<FaDownload />}
                  disabled={!subject || exporting}
                  onClick={() => doExport('pdf')}
                >
                  PDF
                </Button>
                <Button
                  color="secondary"
                  icon={<FaShareAlt />}
                  disabled={!subject || exporting}
                  onClick={doShare}
                >
                  Compartir
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button color="secondary" onClick={() => setStep('diseno')}>
                  Cambiar diseño
                </Button>
                <Button color="secondary" onClick={() => setStep('personalizar')}>
                  Personalizar
                </Button>
              </div>
            </Card>
          )}
        </div>

        <div className="order-1 xl:order-2">
          <Card className="sticky top-4">
            <div className="flex items-center justify-between gap-2 mb-3">
              <h2 className="font-semibold text-e0e3-foreground">Vista previa</h2>
              <span className="text-xs text-e0e3-muted">
                {FLYER_TEMPLATES.find((t) => t.key === templateKey)?.label} ·{' '}
                {getFlyerFormat(formatKey).label}
              </span>
            </div>
            <div className="flex justify-center bg-black/30 rounded-xl p-3 overflow-auto">
              {preview}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FlyerStudio;
